import { observer } from "mobx-react";
import { Card, Input, Button, Checkbox, DatePicker, Alert, Form, FormInstance } from "antd";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { ButtonsRow } from "Components/Form/ButtonsRow";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import { dayjs } from "Config/dayjs";
import * as React from "react";
import { projectsDataStore } from "Stores/ProjectsStore";
import { RouterStore } from "Config/CustomBrowserRouter";
import { emailSearchStore } from "../Stores/EmailSearchStore";
import { EmailSenderSelect } from "./EmailSenderSelect";

interface EmailSearchFormProps extends WithTranslation, ModalComponentProps {}

interface EmailSearchFormState {
  isEmpty: boolean;
}

export const EmailSearchForm = withTranslation(I18N_NS.PROJECT)(
  observer(
    class extends React.Component<EmailSearchFormProps, EmailSearchFormState> {
      readonly state: EmailSearchFormState = { isEmpty: false };
      formRef: React.RefObject<FormInstance> = React.createRef();

      get radioFields(): { label: string; value: string }[] {
        const mailSettings = projectsDataStore.currentProject.mailSettings || {};
        return [
          { label: this.props.t("Inbox"), value: "inbox" },
          // inf user has required access level
          (mailSettings.projectInbox === "READONLY" || mailSettings.projectInbox === "WRITE") && {
            label: this.props.t("Inbox (Project)"),
            value: "inboxProject",
          },

          { label: this.props.t("Sent"), value: "sent" },
          // only for project admins
          projectsDataStore.currentProject.userrole === "ADMIN" && {
            label: this.props.t("Sent (Project)"),
            value: "sentProject",
          },

          { label: this.props.t("Archive"), value: "archived" },
          { label: this.props.t("Linked"), value: "linked" },
          { label: this.props.t("Archived (Project)"), value: "archivedProject" },
          { label: this.props.t("Drafts"), value: "draft" },
        ].filter(Boolean);
      }

      handleSubmit = async (values) => {
        const { emailTypes: _, ...requiredValues } = values;
        // clear falsy values, empty strings etc.
        Object.keys(requiredValues).forEach((key) => {
          if (!requiredValues[key]) {
            delete requiredValues[key];
          }
        });
        // dummy test for empty object. Because we have to fill some fields besides "emailTypes"
        if (JSON.stringify(requiredValues).length === 2) {
          return this.setState({ isEmpty: true });
        } else {
          this.setState({ isEmpty: false });
        }
        try {
          const actualValues: API_MailSearchRequest = JSON.parse(JSON.stringify(values));
          // Convert date to timestamp
          if (actualValues.dateRange) {
            if (actualValues.dateRange[0]) {
              actualValues.dateRange[0] = +dayjs(actualValues.dateRange[0]).startOf("date").format("x");
            }
            if (actualValues.dateRange[1]) {
              actualValues.dateRange[1] = +dayjs(actualValues.dateRange[1]).add(1, "day").startOf("date").format("x");
            }
          }
          actualValues.orConnection = false;
          await emailSearchStore.submitSearch(actualValues);
          RouterStore.push(`/project/${projectsDataStore.projectId}/email/searchResults`);
          this.props.hideModal();
        } catch (err) {
          throw err;
        }
      };

      render() {
        const { t } = this.props;
        const { isEmpty } = this.state;
        const wrapperColProps = { xs: { span: 24 } };

        return (
          <Form onFinish={this.handleSubmit} ref={this.formRef}>
            <Card variant="outlined" styles={{ body: { padding: "24px 0" } }}>
              <Form.Item wrapperCol={wrapperColProps} name="searchText" initialValue={emailSearchStore.searchTextValue}>
                <Input placeholder={t("Message text")} />
              </Form.Item>
              <Form.Item wrapperCol={wrapperColProps} name="subject">
                <Input placeholder={t("Subject")} />
              </Form.Item>
              <Form.Item wrapperCol={wrapperColProps} name="dateRange">
                <DatePicker.RangePicker
                  format={["L", "L"]}
                  className="datepicker-input"
                  placeholder={[t("Start date"), t("End date")]}
                />
              </Form.Item>
              <Form.Item wrapperCol={wrapperColProps} validateTrigger="onBlur" name="sender">
                <EmailSenderSelect />
              </Form.Item>
              {isEmpty && <Alert title={t("Fill some of the fields to submit.")} type="error" />}
            </Card>
            <Card title={t("Email types")} variant="outlined" styles={{ body: { padding: "24px 0" } }}>
              <Form.Item
                name="emailTypes"
                initialValue={[
                  "inbox",
                  "inboxProject",
                  "sent",
                  "sentProject",
                  "archived",
                  "linked",
                  "archivedProject",
                  "draft",
                ]}
              >
                <Checkbox.Group options={this.radioFields} />
              </Form.Item>
            </Card>
            <ButtonsRow>
              <Button type="primary" htmlType="submit" loading={emailSearchStore.state === "LOADING"}>
                {t("Search emails")}
              </Button>
            </ButtonsRow>
          </Form>
        );
      }
    }
  )
);
